import { PageHero, Reveal } from "@/components/Shared";

const sections = [
    {
        title: "1. Information We Do Not Intentionally Collect Through the Website",
        paragraphs: [
            "The website is primarily an informational website. We do not operate user accounts, require registration, or intentionally collect personal information from visitors for advertising, behavioural profiling or routine marketing purposes.",
            "We do not use website forms or other website functionality to routinely collect personal information unless such functionality is specifically made available and information is voluntarily provided by you.",
            "We do not intentionally collect information such as your name, email address, telephone number or other personal details merely because you visit or browse the website.",
        ],
    },
    {
        title: "2. Information You May Voluntarily Provide",
        paragraphs: [
            "If you contact us directly by email, telephone, or another communication channel, we may receive information that you voluntarily provide in connection with your enquiry, proposed engagement, matter, consultation request or other communication.",
            "Depending on the nature of the communication, this may include:",
            "• Name and contact details;\n• Organisation or professional information;\n• Details relating to your enquiry or matter;\n• Documents or information voluntarily shared with us; and\n• Other information that you choose to provide.",
            "We use such information for purposes reasonably connected with the communication, including responding to your enquiry, understanding the matter raised, assessing a potential engagement, communicating with you, and where applicable, administering or performing an engagement.",
            "We do not use information received through an enquiry for unrelated marketing or behavioural profiling purposes.",
        ],
    },
    {
        title: "3. Cookies, Analytics and Tracking Technologies",
        paragraphs: [
            "Our website is not designed to use cookies, tracking pixels, advertising tags, analytics scripts or similar technologies for behavioural advertising, user profiling or marketing purposes.",
            "We do not intentionally track your browsing activity across websites for advertising or profiling.",
            "However, certain technical information may be processed automatically by website hosting, security, infrastructure or communication service providers as part of operating, securing and maintaining the website and related systems. Such information may include technical logs, IP addresses, browser or device information, timestamps and similar technical data, depending on the services and infrastructure used.",
            "Such technical processing is not used by us for behavioural advertising or user profiling.",
        ],
    },
    {
        title: "4. Use of Information",
        paragraphs: [
            "Information voluntarily provided to us may be used for purposes including:",
            "• Responding to enquiries and requests;\n• Communicating regarding potential or existing engagements;\n• Understanding the nature and scope of a matter;\n• Providing or evaluating professional services;\n• Maintaining appropriate business and communication records;\n• Complying with applicable legal, regulatory or professional obligations; and\n• Protecting the security, integrity and legitimate interests of our business and systems.",
            "We do not use information received through direct enquiries for unrelated purposes.",
        ],
    },
    {
        title: "5. Confidentiality and Handling of Communications",
        paragraphs: [
            "We understand that communications relating to forensic investigations, financial intelligence, digital forensics, fraud matters, due diligence and related assignments may contain sensitive or confidential information.",
            "Information voluntarily provided to us in connection with a matter is handled with appropriate discretion and care, subject to:",
            "• Applicable law and regulatory requirements;\n• Contractual obligations;\n• The terms of the relevant engagement;\n• Professional obligations applicable to the matter; and\n• Circumstances in which disclosure may be required or permitted by law or lawful authority.",
            "Where a formal engagement is entered into, confidentiality and information-handling obligations may be further governed by the applicable engagement letter, agreement, statement of work or other contractual documentation.",
        ],
    },
    {
        title: "6. Third-Party Websites and Services",
        paragraphs: [
            "Our website may contain links to third-party websites, platforms, professional profiles or other external resources.",
            "We do not control those third-party websites or their privacy practices. Any information you provide to a third party will be subject to that party’s applicable terms, privacy policy and practices.",
            "We recommend reviewing the relevant privacy notices before providing personal or confidential information to any third-party website or platform.",
        ],
    },
    {
        title: "7. Data Retention",
        paragraphs: [
            "Where we receive information through direct communication or in connection with an enquiry, we retain such information only for as long as reasonably necessary for the relevant purpose, including responding to the enquiry, maintaining appropriate business records, administering an engagement, protecting our legitimate interests, or complying with applicable legal, regulatory or professional obligations.",
            "Retention periods may therefore vary depending on the nature of the communication or engagement and the applicable legal or contractual requirements.",
        ],
    },
    {
        title: "8. Data Security",
        paragraphs: [
            "We take reasonable measures appropriate to the nature of the information and the circumstances to protect information received by us against unauthorised access, use, disclosure, alteration or loss.",
            "However, no method of electronic transmission, storage or communication can be guaranteed to be completely secure.",
            "Accordingly, you should exercise appropriate care before transmitting highly sensitive or confidential information through ordinary email or other unsecured communication channels. Where appropriate, we may provide or agree upon a secure method for sharing information as part of a formal engagement.",
        ],
    },
    {
        title: "9. Personal Data Rights",
        paragraphs: [
            "Where applicable under Indian law, individuals may have rights in relation to their personal data, subject to the conditions, limitations and procedures prescribed by applicable law.",
            "Requests relating to personal information provided directly to us may be made by contacting us using the details provided below. We may require reasonable information to verify the identity and nature of the request before taking action.",
        ],
    },
    {
        title: "10. Changes to this Privacy Policy",
        paragraphs: [
            "We may update this Privacy Policy from time to time to reflect changes in our website, services, technology, legal requirements or information-handling practices.",
            "The updated version will be published on this page with the revised “Last Updated” date.",
        ],
    },
    {
        title: "11. Contact",
        paragraphs: [
            "If you have any questions regarding this Privacy Policy or the handling of information by Infrasic Intelligence, please contact:",
            "INFRASIC INTELLIGENCE (OPC) PRIVATE LIMITED\nC Wing, 202, Nikita Apartment II,\nShimpoli, Borivali West, Mumbai – 400092,\nMaharashtra, India",
            "Email: contactus@infrasic.in\nPhone: +91 76111 81818",
        ],
    },
];

export default function PrivacyPolicy() {
    return (
        <div data-testid="privacy-policy-page">
            <PageHero
                overline="Privacy Policy"
                title="Privacy Policy"
                lead="Last Updated: 30 September 2026"
                testId="privacy-policy-hero"
            />

            <section className="mx-auto max-w-4xl px-6 py-20 lg:px-10">
                <div className="space-y-8">
                    <Reveal>
                        <div className="border border-[#243650] bg-[#0F1B2E] p-8 lg:p-10" data-testid="privacy-intro">
                            <p className="text-base leading-8 text-[#9AA7BF]">
                                Infrasic Intelligence (OPC) Private Limited (“Infrasic Intelligence”, “we”, “us” or “our”) respects the privacy of visitors to our website.
                            </p>
                            <p className="mt-4 text-base leading-8 text-[#9AA7BF]">
                                This Privacy Policy explains how information may be received or handled when you visit www.infrasic.in or communicate with us through the website or the contact details provided on it.
                            </p>
                        </div>
                    </Reveal>

                    {sections.map((section, index) => (
                        <Reveal key={section.title} delay={index * 0.04}>
                            <article className="border border-[#243650] bg-[#0F1B2E] p-8 lg:p-10" data-testid={`privacy-section-${index}`}>
                                <h2 className="font-display text-2xl font-semibold text-[#E8ECF4]">{section.title}</h2>
                                <div className="mt-6 space-y-4 text-base leading-8 text-[#9AA7BF]">
                                    {section.paragraphs.map((paragraph) => (
                                        <p key={paragraph} className="whitespace-pre-line">{paragraph}</p>
                                    ))}
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>
        </div>
    );
}
